/**
 * Step 7: Pet Details
 * Configure pet policy and document each pet on the lease
 */

import type { WizardState, PetPolicy, PetDetail, AssistanceAnimal } from '../../../types/lease-generation';
import { PetCardList } from '../../../components/lease-inputs/PetCardList';
import { AssistanceAnimalCardList } from '../../../components/lease-inputs/AssistanceAnimalCardList';

interface Step7Props {
  state: WizardState;
  updatePets: (update: Partial<PetPolicy>) => void;
  addAnimal: (animal: PetDetail) => void;
  updateAnimal: (index: number, update: Partial<PetDetail>) => void;
  removeAnimal: (index: number) => void;
  addAssistanceAnimal: (animal: AssistanceAnimal) => void;
  updateAssistanceAnimal: (index: number, update: Partial<AssistanceAnimal>) => void;
  removeAssistanceAnimal: (index: number) => void;
  jurisdictionState?: string;
}

export function Step7PetDetails({
  state,
  updatePets,
  addAnimal,
  updateAnimal,
  removeAnimal,
  addAssistanceAnimal,
  updateAssistanceAnimal,
  removeAssistanceAnimal,
  jurisdictionState,
}: Step7Props) {
  const { pets } = state;
  const isWashington = jurisdictionState === 'WA';

  const inputClass =
    'w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
  const labelClass = 'block mb-1 text-sm font-medium text-foreground';

  return (
    <div className="flex flex-col gap-6" data-testid="step-pet-details">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Pet Details</h3>
        <p className="text-sm text-muted-foreground">
          Configure pet policy and document each pet that will be on the lease.
        </p>
      </div>

      {/* Pets Allowed toggle */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <label htmlFor="pets-allowed-toggle" className="text-sm font-medium text-foreground cursor-pointer">
            Pets allowed on the premises
          </label>
          <button
            id="pets-allowed-toggle"
            type="button"
            role="switch"
            aria-checked={pets.allowed}
            aria-label="Pets allowed on the premises"
            data-testid="pets-allowed-toggle"
            onClick={() => updatePets({ allowed: !pets.allowed })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
              pets.allowed ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ${
                pets.allowed ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {pets.allowed ? (
        <>
          {/* Policy fields */}
          <div className="flex flex-col gap-4">
            <div>
              <label htmlFor="max-pet-count" className={labelClass}>Max Number of Pets</label>
              <input
                id="max-pet-count"
                type="number"
                data-testid="max-pet-count"
                value={pets.maxCount ?? ''}
                min={1}
                step={1}
                onChange={e =>
                  updatePets({ maxCount: e.target.value !== '' ? Number(e.target.value) : undefined })
                }
                className={inputClass}
                placeholder="e.g. 2"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="pet-deposit-input" className={labelClass}>Pet Deposit ($)</label>
                <input
                  id="pet-deposit-input"
                  type="number"
                  data-testid="pet-deposit-input"
                  value={pets.deposit != null ? pets.deposit / 100 : ''}
                  min={0}
                  step={1}
                  onChange={e =>
                    updatePets({ deposit: e.target.value !== '' ? Math.round(Number(e.target.value) * 100) : undefined })
                  }
                  className={inputClass}
                  placeholder="e.g. 500"
                />
              </div>
              <div>
                <label htmlFor="pet-monthly-fee-input" className={labelClass}>Monthly Pet Rent ($)</label>
                <input
                  id="pet-monthly-fee-input"
                  type="number"
                  data-testid="pet-monthly-fee-input"
                  value={pets.monthlyFee != null ? pets.monthlyFee / 100 : ''}
                  min={0}
                  step={1}
                  onChange={e =>
                    updatePets({ monthlyFee: e.target.value !== '' ? Math.round(Number(e.target.value) * 100) : undefined })
                  }
                  className={inputClass}
                  placeholder="e.g. 50"
                />
              </div>
            </div>
          </div>

          {pets._claimedPetsHint && (
            <div
              data-testid="pet-claimed-hint"
              role="note"
              className="text-xs text-warning bg-warning/10 border border-warning/20 rounded px-4 py-3"
            >
              <span className="font-medium">Application note: </span>
              {pets._claimedPetsHint}
            </div>
          )}

          {/* Pets list */}
          <PetCardList
            animals={pets.animals}
            addAnimal={addAnimal}
            updateAnimal={updateAnimal}
            removeAnimal={removeAnimal}
            prefilled={pets._prefilled}
          />
        </>
      ) : (
        <div className="py-6 text-center bg-accent/50 rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">
            No pets will be permitted on the premises. Toggle above to allow pets and configure the
            pet policy.
          </p>
        </div>
      )}

      {/* Assistance Animals section — always visible regardless of pet policy */}
      <div data-testid="step-assistance-animals" className="flex flex-col gap-4 border-t border-border pt-8 mt-2">
        <div>
          <h3 className="text-base font-semibold text-foreground mb-1">Assistance Animals (ESA &amp; Service Animals)</h3>
          <p className="text-sm text-muted-foreground">
            Under the Fair Housing Act (FHA), landlords must make reasonable accommodations for tenants with
            disabilities who require emotional support animals or service animals. These animals are not subject to
            no-pets rules, pet restrictions, pet deposits, or pet fees, but they must still follow rules that apply to
            all tenants (such as nuisance and sanitation), and approval may be withdrawn if an animal poses a direct
            threat or causes substantial damage.
          </p>
        </div>

        {isWashington && (
          <div
            data-testid="wa-7day-note"
            role="note"
            className="bg-primary/10 border border-primary/20 text-primary rounded px-3 py-2 text-xs"
          >
            Washington law (RCW 49.60.222) requires landlords to respond to reasonable accommodation requests
            within 7 calendar days of receiving proper documentation.
          </div>
        )}

        <AssistanceAnimalCardList
          animals={pets.assistanceAnimals}
          addAssistanceAnimal={addAssistanceAnimal}
          updateAssistanceAnimal={updateAssistanceAnimal}
          removeAssistanceAnimal={removeAssistanceAnimal}
        />
      </div>
    </div>
  );
}
